import { useMemo, useState, useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { Col, Row, Card, CardBody, Button } from "reactstrap"

// Custom Components
import TableContainer from "../../../components/Common/TableContainer"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import DeleteModal from "../../../components/Common/DeleteModal"

// Custom Hooks
import { useDeleteVehicleStock, useVehicleStocks } from "hooks/stockHook"

import { useListParams } from "hooks/useListParams"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { downloadVehiclePdf, downloadVehicleStockPdf } from "helpers/pdf_helper"
import { useResponsiveCollapse } from "hooks/useWindowSize"
import StockFilters from "./StockFilters"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import ImageViewer from "components/Common/ImageViewer/ImageViewer"
import { getStockColumns } from "components/Common/stock-table/stockColumns"
import CarStockWrapper from "pages/VehicleImporterPages/ImporterView/tabs/stocks/components/CarStockList/CarStockWrapper"
import BulkVehicleUploader from "./BulkVehicleUploader"

const StockList = () => {
  document.title = "Stock List"
  const navigate = useNavigate()

  const [stock, setStock] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [selectedImageUrl, setSelectedImageUrl] = useState("")
  const [imageTitle, setImageTitle] = useState("Auction Sheet")

  const openImageViewer = url => {
    setSelectedImageIndex(0)
    setViewerOpen(true)
    setSelectedImageUrl(url || "")
  }

  const {
    params,
    setPage,
    resetParams,
    hasActiveFilters,
    updateParams,
  } = useListParams({
    page: 1,
    isShowCount: true,
  })

  const queryParams = useMemo(
    () => ({
      page: params.page,
      per_page: 10,
      ...params,
    }),
    [params]
  )

  const {
    data: stocksResponse,
    isLoading,
    refetch: stocksRefetch,
    isRefetching,
  } = useVehicleStocks(queryParams)

  const { mutateAsync: deleteStock, isPending: stockDeleteLoading } =
    useDeleteVehicleStock()

  const handleAddNewClick = () => {
    navigate("/stock-add-edit")
  }

  const handleStockClick = useCallback(stockData => {
    if (stockData?.stock_id) {
      navigate(`/stock-add-edit/${stockData?.stock_id}`)
    }
  }, [])

  const onClickDelete = useCallback(stockData => {
    setStock(stockData)
    setDeleteModal(true)
  }, [])

  const handleDeleteStock = useCallback(async () => {
    if (stock?.id) {
      try {
        const res = await deleteStock(stock.id)
        toast.success(res?.message ?? "Stock deleted successfully.")
        setDeleteModal(false)
      } catch (error) {
        console.error("Failed to delete stock:", error)
        toast.error("Failed to delete stock.")
      }
    }
  }, [stock, deleteStock])

  const handleListRefresh = () => {
    stocksRefetch()
  }

  const onClickDownload = async stockId => {
    try {
      await downloadVehiclePdf(stockId)
    } catch (error) {}
  }

  const { isMobile, toggleRow, isExpanded } = useResponsiveCollapse()

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false)
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)

  // ✅ Auto-hide filters on mobile by default
  useEffect(() => {
    if (isMobile) setIsFiltersOpen(false)
    else setIsFiltersOpen(false)
  }, [isMobile])

  const columns = useMemo(
    () =>
      getStockColumns({
        navigate,
        isMobile,
        toggleRow,
        isExpanded,
        handleStockClick,
        onClickDelete,
        onClickDownload,
        openImageViewer,
        setImageTitle,
      }),
    [navigate, isMobile, toggleRow, isExpanded]
  )

  // ✅ Extracted clean data
  const stocksData = stocksResponse?.data?.vehicles?.data ?? []
  const paginationInfo = stocksResponse?.data?.vehicles ?? {}
  const filteredOptions = stocksResponse?.data?.counts || {}

  const handleStockDownload = async () => {
    try {
      await downloadVehicleStockPdf(queryParams)
    } catch (error) {}
  }

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteStock}
        loading={stockDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <ImageViewer
        images={[selectedImageUrl]}
        initialIndex={selectedImageIndex}
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        title={imageTitle}
      />
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Stock" breadcrumbItem="Stock List" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="border-bottom ">
                  <Row className="justify-content-between align-items-center">
                    <Col xs={12} sm={6}>
                      <h5 className="mb-0">
                        Stock List{" "}
                        <span className="badge badge-soft-info">
                          {paginationInfo?.total ?? 0}
                        </span>
                      </h5>
                    </Col>
                    <Col xs={12} sm={6}>
                      <div className="text-sm-end mt-2 mt-sm-0">
                        <Button
                          color="primary"
                          onClick={handleAddNewClick}
                          className="me-1"
                        >
                          Add New Stock
                        </Button>

                        <ButtonTooltip
                          type="refresh"
                          btnId="refreshStockList"
                          onClick={handleListRefresh}
                          loading={isRefetching}
                          color="light"
                          className="btn btn-light me-1"
                        />
                        <CarStockWrapper
                          {...params}
                          btn={{ color: "info" }}
                          allowedKeys={[
                            "page",
                            "per_page",
                            "isShowCount",
                          ]}
                        />

                        <BulkVehicleUploader />

                        {hasActiveFilters(["isShowCount"]) && (
                          <Button
                            className="ms-1"
                            color="danger"
                            onClick={resetParams}
                          >
                            Reset Filters
                          </Button>
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
                <CardBody>
                  <Row>
                    <Col sm={5} lg={3} className="mb-3">
                      <GlobalInputSearch
                        value={params.search}
                        onChange={val =>
                          updateParams(prev => ({
                            ...prev,
                            search: val,
                            page: 1,
                          }))
                        }
                      />
                    </Col>
                    <Col>
                      <div className="mb-2 d-flex justify-content-end">
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => setIsFiltersOpen(prev => !prev)}
                        >
                          {isFiltersOpen ? "Hide Filters ▲" : "Show Filters ▼"}
                        </button>
                      </div>
                    </Col>
                  </Row>
                  <StockFilters
                    params={params}
                    updateParams={updateParams}
                    isAdvancedOpen={isAdvancedOpen}
                    isFiltersOpen={isFiltersOpen}
                    setIsAdvancedOpen={setIsAdvancedOpen}
                    data={filteredOptions}
                  />
                  <TableContainer
                    columns={columns}
                    data={stocksData}
                    isPagination={true}
                    paginationInfo={paginationInfo}
                    onPageChange={setPage}
                    loading={isLoading || isRefetching}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </>
  )
}

export default StockList
